import { trackApiError } from '../../utils/datadog';
import { ApiError, extractServerMessage } from './ApiError';
import type { Language } from '../types/Language';

export const fetchWithErrorHandling = <T>(
    url: RequestInfo,
    options: RequestInit,
    language: Language,
    ): Promise<T> =>
    {
        const method = options.method || 'GET';
        const requestUrl = getRequestUrl(url);

        return fetch(url, options)
            .catch((e: unknown) => {
                const error = new Error(`Network request failed: ${toErrorMessage(e)}`);
                trackApiError(
                    error,
                    requestUrl,
                    method,
                    undefined,
                    { type: 'network_error', originalError: toErrorMessage(e) }
                );
                throw new ApiError(language.loginFailure);
            })
            .then((res) => handleErrors(res, language, requestUrl, method))
            .then((res) => res?.json());
    };

function getRequestUrl(url: RequestInfo): string {
    if (typeof url === 'string') return url;
    if (url instanceof URL) return url.toString();
    return url.url;
}

function toErrorMessage(value: unknown): string {
    if (value instanceof Error) return value.message;
    if (typeof value === 'string') return value;

    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
}

async function handleErrors(res: void | Response, language: Language, requestUrl: string, method: string) {
    if (!res) {
        trackApiError(
            new Error('No response received from API'),
            requestUrl,
            method,
            undefined,
            { type: 'empty_response' }
        );
        throw new ApiError(language.loginFailure);
    }

    if (res.ok) return res;

    let responseBody: unknown = undefined;
    try {
        const raw = await res.clone().text();
        if (raw) {
            try {
                responseBody = JSON.parse(raw);
            } catch {
                responseBody = raw;
            }
        }
    } catch {
        responseBody = '[unreadable response body]';
    }

    const error = new Error(`API request failed with status ${res.status} ${res.statusText}`.trim());
    trackApiError(
        error,
        requestUrl || res.url,
        method,
        res.status,
        {
            statusText: res.statusText,
            responseBody,
        }
    );

    if (res.status === 401) {
        throw new ApiError(language.incorrectCreds, { status: res.status });
    }

    // Surface the message CABES sent back when there is one, so users see the
    // actual reason instead of the generic fallback.
    const serverMessage = extractServerMessage(responseBody);
    if (serverMessage) {
        throw new ApiError(serverMessage, { status: res.status, serverMessage });
    }

    // A 400 means the request itself was rejected, so the translated bad-request
    // message is more actionable than the generic fallback.
    if (res.status === 400) {
        throw new ApiError(language.badRequest, { status: res.status });
    }

    throw new ApiError(language.loginFailure, { status: res.status });
}
